import { executionTypes, initialExecutionStatus } from "containers/ExecutionFilter/constants";
import {
    getFilterStorageKey,
} from "containers/ExecutionFilter/filterStorage";
import { History } from "history";
import { isEqual } from "lodash";
import { useCallback, useMemo } from "react";
import { FilteredValues } from "./interfaces";
import { getValuesFromQuery, useInitialLocationValue, useSetQueryString } from "./utils";

export const useExecutionFilter = (
    queryParams: { [key: string]: string },
    history: History,
    hideTopLevelExecutionFilter: boolean,
    filterContext?: { recordType: string; recordId: string },
    isContextPending = false
) => {
    const filterStorageKey = useMemo(
        () =>
            filterContext?.recordId
                ? getFilterStorageKey({
                      page: "execution-filter",
                      recordType: filterContext.recordType,
                      recordId: filterContext.recordId,
                  })
                : undefined,
        [filterContext?.recordId, filterContext?.recordType]
    );

    const {
        setQueryString,
        setInitialQueryString,
        setCustomFilter,
        effectiveQueryParams,
        hasStoredFilter,
        isFilterInitialized,
    } = useSetQueryString(queryParams, history, filterStorageKey, isContextPending);

    const {
        findBooleanValueInQuery: findStoredBooleanValue,
        findValueInFilterQuery: findStoredValueInFilter,
        findEncodedValueInFilterQuery: findStoredEncodedValue,
        findEmptyString: findStoredEmptyString,
        findLocation: findStoredLocation,
    } = useMemo(() => getValuesFromQuery(effectiveQueryParams), [effectiveQueryParams]);

    const { initialProjectOrBundleLocationValue } = useInitialLocationValue();

    const findStoredProjectOrBundleLocation = useCallback(
        () => findStoredLocation(initialProjectOrBundleLocationValue),
        [findStoredLocation, initialProjectOrBundleLocationValue]
    );

    const filteredValues: FilteredValues = useMemo(
        () => ({
            name: findStoredEncodedValue("name"),
            location: findStoredProjectOrBundleLocation(),
            status: findStoredValueInFilter("status").array,
            created_at_gt: findStoredValueInFilter("created_at", "gt").foundValue,
            created_at_lt: findStoredValueInFilter("created_at", "lt").foundValue,
            include_deleted: findStoredBooleanValue("include_deleted", false),
            hide_deleted: findStoredBooleanValue("hide_deleted", true),
            no_parent_id: findStoredEmptyString("parent_execution_id"),
            execution_type: findStoredValueInFilter("execution_type").array,
        }),
        [
            findStoredEncodedValue,
            findStoredProjectOrBundleLocation,
            findStoredValueInFilter,
            findStoredBooleanValue,
            findStoredEmptyString,
        ]
    );

    const initialFilter = useMemo(() => {
        const commonFilter = {
            status: initialExecutionStatus,
            created_at_gt: "",
            created_at_lt: "",
            execution_type: executionTypes,
            hide_deleted: true,
            include_deleted: false,
        };
        return hideTopLevelExecutionFilter
            ? { ...commonFilter }
            : {
                  ...commonFilter,
                  no_parent_id: true,
              };
    }, [hideTopLevelExecutionFilter]);

    const initialFilterWithLocation = useMemo(() => {
        return {
            ...initialFilter,
            location: initialProjectOrBundleLocationValue,
        };
    }, [initialFilter, initialProjectOrBundleLocationValue]);

    const searchEverywhereFilter = useMemo(() => {
        const filter = {
            ...initialFilterWithLocation,
            name: filteredValues.name,
            no_parent_id: false,
            include_deleted: true,
            hide_deleted: true,
        };
        return filter;
    }, [filteredValues.name, initialFilterWithLocation]);
    const canSearchEverywhere = useMemo(
        () => !isEqual(searchEverywhereFilter, filteredValues),
        [filteredValues, searchEverywhereFilter]
    );

    const setInitialFilterHandler = useCallback(
        (historyMethod?: "push" | "replace") => {
            const additionalParams: { customFilter?: string } = {};
            if (queryParams.customFilter) {
                additionalParams.customFilter = String(queryParams.customFilter);
            }
            setInitialQueryString(initialFilter, historyMethod, additionalParams);
        },
        [initialFilter, queryParams.customFilter, setInitialQueryString]
    );

    return {
        filteredValues,
        setQueryString,
        setInitialQueryString,
        canSearchEverywhere,
        onChangeHandlers: {
            setInitialFilterHandler,
        },
        setCustomFilter,
        initialFilterWithLocation,
        effectiveQueryParams,
        hasStoredFilter,
        isFilterInitialized,
    };
};

